import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { UPLOADS_URL, generateSlug } from './config';
import { apiGet } from './api';
import { formatNumar, formatPret } from './format';
import { STATUS, combustibilScurt } from './constants';
import useIsMobile from './useIsMobile';
import {
    TbManualGearbox,
    TbWheel,
    TbEngine,
    TbGasStation,
    TbCalendarTime,
    TbRoad,
    TbFilter,
    TbArrowsSort,
    TbX
} from "react-icons/tb";
import './App.css';

function Home() {
    const [allMasini, setAllMasini] = useState([]);
    const [masiniAfisat, setMasiniAfisat] = useState([]);
    const [loadingCars, setLoadingCars] = useState(true);
    const [fetchError, setFetchError] = useState(false);
    const filterDebounceRef = useRef(null);
    const isInitialMount = useRef(true);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [sortOrder, setSortOrder] = useState("default");
    const isMobile = useIsMobile();
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 9;

    const [filtre, setFiltre] = useState({
        marca: [],
        pretMin: "", pretMaxim: "",
        anMin: "", anMax: "",
        kmMin: "", kmMax: "",
        cutieViteze: [],
        combustibil: [],
        tractiune: []
    });

    const [openSections, setOpenSections] = useState({
        pret: false,
        marca: false,
        specs: false,
        cutie: false,
        combustibil: false,
        tractiune: false
    });

    const [dragY, setDragY] = useState(0);
    const dragStartY = React.useRef(null);
    const isDragging = React.useRef(false);

    // Lock body scroll when mobile filters open (iOS-safe)
    useEffect(() => {
        if (showMobileFilters) {
            const scrollY = window.scrollY;
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
        } else {
            const scrollY = Math.abs(parseInt(document.body.style.top || '0'));
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            window.scrollTo(0, scrollY);
            setDragY(0);
        }
        return () => {
            const scrollY = Math.abs(parseInt(document.body.style.top || '0'));
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            if (scrollY) window.scrollTo(0, scrollY);
        };
    }, [showMobileFilters]);

    const onSheetTouchStart = (e) => {
        dragStartY.current = e.touches[0].clientY;
        isDragging.current = true;
    };

    const onSheetTouchMove = (e) => {
        if (!isDragging.current) return;
        const delta = e.touches[0].clientY - dragStartY.current;
        if (delta > 0) setDragY(delta);
    };

    const onSheetTouchEnd = () => {
        isDragging.current = false;
        if (dragY > 120) {
            setShowMobileFilters(false);
        } else {
            setDragY(0);
        }
    };

    const primaryOrange = "#ff8e18";
    const trustNavy = "#1c2e4a";

    useEffect(() => {
        document.title = 'Showroom | SPCheapCars';
        apiGet('/parc-auto')
            .then(data => {
                setAllMasini(data);
                setMasiniAfisat(data.filter(m => m.status !== STATUS.INACTIV));
            })
            .catch(() => { setFetchError(true); })
            .finally(() => setLoadingCars(false));
    }, []);

    useEffect(() => {
        if (isInitialMount.current) { isInitialMount.current = false; return; }
        clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = setTimeout(() => {
            const params = new URLSearchParams();
            filtre.marca.forEach(m => params.append('marca', m));
            if (filtre.pretMin) params.set('pretMin', filtre.pretMin);
            if (filtre.pretMaxim) params.set('pretMax', filtre.pretMaxim);
            if (filtre.anMin) params.set('anMin', filtre.anMin);
            if (filtre.anMax) params.set('anMax', filtre.anMax);
            if (filtre.kmMin) params.set('kmMin', filtre.kmMin);
            if (filtre.kmMax) params.set('kmMax', filtre.kmMax);
            filtre.combustibil.forEach(c => params.append('combustibil', c));
            filtre.cutieViteze.forEach(c => params.append('cutieViteze', c));
            filtre.tractiune.forEach(t => params.append('tractiune', t));
            const queryStr = params.toString();
            apiGet(queryStr ? `/parc-auto?${queryStr}` : '/parc-auto')
                .then(data => setMasiniAfisat(data.filter(m => m.status !== STATUS.INACTIV)))
                .catch(() => setFetchError(true));
        }, 400);
        return () => clearTimeout(filterDebounceRef.current);
    }, [filtre]);

    const toggleSection = (section) => {
        setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const getCount = (key, value) => {
        if (!value) return 0;
        return allMasini.filter(m => m.status !== 'INACTIV' && m[key] && m[key].toString().toLowerCase() === value.toLowerCase()).length;
    };

    const roCollator = new Intl.Collator('ro');
    const masiniActive = allMasini.filter(m => m.status !== STATUS.INACTIV);
    const uniqueBrands = [...new Set(masiniActive.map(m => m.marca))].sort(roCollator.compare);
    const uniqueFuels = [...new Set(masiniActive.map(m => m.combustibil))].filter(Boolean).sort(roCollator.compare);
    const uniqueDrivetrains = [...new Set(masiniActive.map(m => m.tractiune))].filter(Boolean).sort(roCollator.compare);

    const masiniFiltrate = masiniAfisat.slice().sort((a, b) => {
        if (sortOrder === 'pret_crescator') return a.pret - b.pret;
        if (sortOrder === 'pret_descrescator') return b.pret - a.pret;
        return 0;
    });

    const totalPages = Math.ceil(masiniFiltrate.length / ITEMS_PER_PAGE);
    const masiniDisplay = isMobile
        ? masiniFiltrate.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
        : masiniFiltrate;

    useEffect(() => {
        setCurrentPage(1);
    }, [filtre, sortOrder, masiniAfisat]);

    const scrollToMasini = () => {
        const element = document.getElementById('masini');
        if (!element) return;
        const offset = 100;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    };

    const goToPage = (page) => {
        setCurrentPage(page);
        scrollToMasini();
    };

    const handleFilterChange = (e) => {
        if (e.target.type === 'number' && e.target.value < 0) {
            return;
        }
        setFiltre(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const toggleTag = (field, value) => {
        setFiltre(prev => {
            const currentList = prev[field]; // Lista actuala (ex: ["BMW"])

            if (currentList.includes(value)) {
                // Daca exista deja, il scoatem (Filter out)
                return { ...prev, [field]: currentList.filter(item => item !== value) };
            } else {
                // Daca nu exista, il adaugam la lista
                return { ...prev, [field]: [...currentList, value] };
            }
        });
    };

    const resetFilters = () => {
        setFiltre({
            marca: [],
            pretMin: "", pretMaxim: "",
            anMin: "", anMax: "",
            kmMin: "", kmMax: "",
            cutieViteze: [],
            combustibil: [],
            tractiune: []
        });
    };

    const getImaginePrincipala = (masina) => {
        if (masina.imagini && masina.imagini.length > 0) {
            const img = masina.imagini[0];
            // miniatura (480px) e suficienta pentru card; originalul ramane pentru pagina de detalii
            return `${UPLOADS_URL}/${img.thumbnail || img.numeFisier}`;
        }
        return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' fill='%23e5e7eb'%3E%3Crect width='400' height='300' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='16' fill='%239ca3af'%3EF%C4%83r%C4%83 Poz%C4%83%3C/text%3E%3C/svg%3E";
    };

    const styles = {
        filterPanel: {
            background: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
            borderRadius: '16px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
            border: '1px solid rgba(0,0,0,0.06)',
            position: 'sticky',
            top: '100px',
            overflow: 'hidden',
            alignSelf: 'flex-start',
            zIndex: 10,
            /* Ascundem scrollbar-ul standard dar pastram functionalitatea */
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge
        },
        filterHeader: { background: `linear-gradient(135deg, ${trustNavy} 0%, #2a4266 100%)`, padding: '18px 20px', position: 'relative', overflow: 'hidden' },
        filterHeaderPattern: { position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: `radial-gradient(circle at 100% 0%, ${primaryOrange}20 0%, transparent 70%)`, pointerEvents: 'none' },
        filterTitle: { color: '#ffffff', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' },
        filterIcon: { width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
        resetBtn: { background: `rgba(255,255,255,0.15)`, border: `1px solid rgba(255,255,255,0.2)`, padding: '6px 12px', borderRadius: '6px', color: 'rgba(255,255,255,0.95)', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease', textTransform: 'uppercase', letterSpacing: '0.5px' },
        filterBody: { padding: '20px' },
        sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '14px 16px' : '10px 14px', margin: '0 -20px', marginTop: isMobile ? '0' : '-4px', cursor: 'pointer', transition: 'background 0.2s ease', borderRadius: isMobile ? '0' : '8px', minHeight: isMobile ? '52px' : 'auto' },
        sectionTitle: { fontSize: isMobile ? '0.875rem' : '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: trustNavy, display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '8px' },
        sectionIcon: { width: isMobile ? '28px' : '24px', height: isMobile ? '28px' : '24px', borderRadius: isMobile ? '7px' : '6px', background: `linear-gradient(135deg, ${primaryOrange}15 0%, ${primaryOrange}05 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? '0.8rem' : '0.7rem' },
        sectionDivider: { height: '1px', background: isMobile ? '#f0f0f0' : 'linear-gradient(90deg, #e8e8e8 0%, transparent 100%)', margin: isMobile ? '4px 0' : '16px 0' },
        inputWrapper: { display: 'flex', gap: '8px', alignItems: 'center' },
        input: { border: '1px solid #e8e8e8', borderRadius: '10px', padding: isMobile ? '13px 12px' : '10px 12px', fontSize: isMobile ? '1rem' : '0.875rem', transition: 'all 0.2s ease', background: '#fafafa', textAlign: 'center', width: '100%' },
        select: { border: '1px solid #e8e8e8', borderRadius: '10px', padding: isMobile ? '13px 14px' : '10px 14px', fontSize: isMobile ? '1rem' : '0.875rem', background: '#fafafa', cursor: 'pointer', width: '100%' },
        tagBtn: { padding: isMobile ? '11px 18px' : '10px 16px', minHeight: isMobile ? '44px' : '36px', borderRadius: isMobile ? '22px' : '20px', border: '1px solid #e0e0e0', background: '#fafafa', fontSize: isMobile ? '0.875rem' : '0.75rem', fontWeight: '500', color: '#555', cursor: 'pointer', transition: 'all 0.2s ease', display: 'inline-flex', alignItems: 'center', gap: isMobile ? '5px' : '4px' },
        tagBtnActive: { padding: isMobile ? '11px 18px' : '10px 16px', minHeight: isMobile ? '44px' : '36px', borderRadius: isMobile ? '22px' : '20px', border: `2px solid ${primaryOrange}`, background: `linear-gradient(135deg, ${primaryOrange}15 0%, ${primaryOrange}05 100%)`, fontSize: isMobile ? '0.875rem' : '0.75rem', fontWeight: isMobile ? '700' : '600', color: trustNavy, cursor: 'pointer', transition: 'all 0.2s ease', display: 'inline-flex', alignItems: 'center', gap: isMobile ? '5px' : '4px' },
        vehicleCard: { background: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.04)', transition: 'all 0.3s ease', height: '100%', display: 'flex', flexDirection: 'column' },
        // 4:3 in loc de inaltime fixa: pe mobil cardul e lat cat ecranul si
        // poza nu mai iese decupata ca o fasie ingusta
        imageWrapper: { position: 'relative', aspectRatio: '4 / 3', overflow: 'hidden' },
        yearBadge: { position: 'absolute', top: '12px', left: '12px', background: 'rgba(15,26,43,0.72)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', color: 'white', padding: '5px 11px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.5px', boxShadow: '0 2px 10px rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.12)' },
        cardBody: { padding: '20px' },
        priceBox: { background: `linear-gradient(135deg, ${trustNavy} 0%, #2a4266 100%)`, borderRadius: '12px', padding: '14px 18px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', gap: '2px', position: 'relative', overflow: 'hidden', marginTop: '16px' },
        priceBoxAccent: { position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: primaryOrange },
        price: { fontSize: '1.5rem', fontWeight: '700', color: 'white', fontFamily: 'var(--font-display)' },
        priceCurrency: { color: primaryOrange, fontWeight: '700', fontSize: '1.5rem', marginLeft: '4px', fontFamily: 'var(--font-display)' },
        // nowrap + padding mai mic ca "Plug-in Hybrid" si "198.000 km" sa incapa pe un singur rand
        cardFooter: { background: '#f8f9fa', padding: '12px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px', borderTop: '1px solid #f0f0f0', marginTop: 'auto' },
        footerStat: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px', fontSize: '0.75rem', color: '#6c757d', whiteSpace: 'nowrap' },
        resultsHeader: { background: 'white', borderRadius: '12px', padding: '16px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }
    };

    // --- LOGICA DE RENDER PENTRU FILTRE (REUTILIZABILA) ---
    const renderFilters = () => (
        <div style={styles.filterBody}>
            {/* MARCA */}
            {/* MARCA */}
            <div style={styles.sectionHeader} onClick={() => toggleSection('marca')}>
                <span style={styles.sectionTitle}>
                    <span style={styles.sectionIcon}><i className="bi bi-car-front" style={{color: primaryOrange}}></i></span>
                    Marca
                </span>
                <i className={`bi bi-chevron-${openSections.marca ? 'up' : 'down'}`} style={{color: '#aaa', fontSize: '0.8rem'}}></i>
            </div>
            {openSections.marca && (
                <div className="mt-3 mb-2">
                    {/* AICI AM SCHIMBAT DIN SELECT IN BUTOANE */}
                    <div className="d-flex flex-wrap gap-2">
                        {uniqueBrands.map(marca => (
                            <button
                                key={marca}
                                // VERIFICARE NOUA: .includes()
                                style={filtre.marca.includes(marca) ? styles.tagBtnActive : styles.tagBtn}
                                onClick={() => toggleTag('marca', marca)}
                            >
                                {/* VERIFICARE NOUA: .includes() */}
                                {filtre.marca.includes(marca) && <i className="bi bi-check-circle-fill me-1" style={{color: primaryOrange, fontSize: '0.65rem'}}></i>}
                                {marca}
                                <span style={{opacity: 0.5, marginLeft: '2px'}}>({getCount('marca', marca)})</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <div style={styles.sectionDivider}></div>

            {/* PRET */}
            <div style={styles.sectionHeader} onClick={() => toggleSection('pret')}>
                <span style={styles.sectionTitle}>
                    <span style={styles.sectionIcon}><i className="bi bi-currency-euro" style={{color: primaryOrange}}></i></span>
                    Pret (EUR)
                </span>
                <i className={`bi bi-chevron-${openSections.pret ? 'up' : 'down'}`} style={{color: '#aaa', fontSize: '0.8rem'}}></i>
            </div>
            {openSections.pret && (
                <div className="mt-3 mb-2">
                    <div style={styles.inputWrapper}>
                        <input name="pretMin" type="number" inputMode="numeric" min="0" style={styles.input} placeholder="Min" value={filtre.pretMin} onChange={handleFilterChange} />
                        <span style={{color: '#ccc', fontWeight: '300'}}>—</span>
                        <input name="pretMaxim" type="number" inputMode="numeric" style={styles.input} placeholder="Max" value={filtre.pretMaxim} onChange={handleFilterChange} />
                    </div>
                </div>
            )}
            <div style={styles.sectionDivider}></div>

            {/* AN & KILOMETRAJ */}
            <div style={styles.sectionHeader} onClick={() => toggleSection('specs')}>
                <span style={styles.sectionTitle}>
                    <span style={styles.sectionIcon}><TbCalendarTime size={18} color={primaryOrange} /></span>
                    An & Kilometraj
                </span>
                <i className={`bi bi-chevron-${openSections.specs ? 'up' : 'down'}`} style={{color: '#aaa', fontSize: '0.8rem'}}></i>
            </div>
            {openSections.specs && (
                <div className="mt-3 mb-2">
                    <label className="text-uppercase small fw-bold text-muted mb-2">An Fabricatie</label>
                    <div style={styles.inputWrapper} className="mb-3">
                        <input name="anMin" type="number" inputMode="numeric" style={styles.input} placeholder="  Min" value={filtre.anMin} onChange={handleFilterChange} />
                        <span style={{color: '#ccc', fontWeight: '300'}}>—</span>
                        <input name="anMax" type="number" inputMode="numeric" style={styles.input} placeholder="  Max" value={filtre.anMax} onChange={handleFilterChange} />
                    </div>
                    <label className="text-uppercase small fw-bold text-muted mb-2">Kilometri</label>
                    <div style={styles.inputWrapper}>
                        <input name="kmMin" type="number" inputMode="numeric" style={styles.input} placeholder="Min" value={filtre.kmMin} onChange={handleFilterChange} />
                        <span style={{color: '#ccc', fontWeight: '300'}}>—</span>
                        <input name="kmMax" type="number" inputMode="numeric" style={styles.input} placeholder="Max" value={filtre.kmMax} onChange={handleFilterChange} />
                    </div>
                </div>
            )}
            <div style={styles.sectionDivider}></div>

            {/* COMBUSTIBIL */}
            <div style={styles.sectionHeader} onClick={() => toggleSection('combustibil')}>
                <span style={styles.sectionTitle}>
                    <span style={styles.sectionIcon}><TbGasStation size={18} color={primaryOrange} /></span>
                    Combustibil
                </span>
                <i className={`bi bi-chevron-${openSections.combustibil ? 'up' : 'down'}`} style={{color: '#aaa', fontSize: '0.8rem'}}></i>
            </div>
            {openSections.combustibil && (
                <div className="mt-3 mb-2">
                    <div className="d-flex flex-wrap gap-2">
                        {uniqueFuels.map(combustibil => (
                            <button
                                key={combustibil}
                                style={filtre.combustibil.includes(combustibil) ? styles.tagBtnActive : styles.tagBtn}
                                onClick={() => toggleTag('combustibil', combustibil)}
                            >
                                {filtre.combustibil.includes(combustibil) && <i className="bi bi-check-circle-fill me-1" style={{color: primaryOrange, fontSize: '0.65rem'}}></i>}
                                {combustibil}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <div style={styles.sectionDivider}></div>

            {/* CUTIE VITEZE */}
            <div style={styles.sectionHeader} onClick={() => toggleSection('cutie')}>
                <span style={styles.sectionTitle}>
                    <span style={styles.sectionIcon}><TbManualGearbox size={20} color={primaryOrange} /></span>
                    Cutie Viteze
                </span>
                <i className={`bi bi-chevron-${openSections.cutie ? 'up' : 'down'}`} style={{color: '#aaa', fontSize: '0.8rem'}}></i>
            </div>
            {openSections.cutie && (
                <div className="mt-3 mb-2">
                    <div className="d-flex flex-wrap gap-2">
                        {['Automată', 'Manuală'].map(c => (
                            <button
                                key={c}
                                // MODIFICAT AICI: .includes()
                                style={filtre.cutieViteze.includes(c) ? styles.tagBtnActive : styles.tagBtn}
                                onClick={() => toggleTag('cutieViteze', c)}
                            >
                                {/* MODIFICAT AICI: .includes() */}
                                {filtre.cutieViteze.includes(c) && <i className="bi bi-check-circle-fill me-1" style={{color: primaryOrange, fontSize: '0.65rem'}}></i>}
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <div style={styles.sectionDivider}></div>

            {/* TRACTIUNE */}
            <div style={styles.sectionHeader} onClick={() => toggleSection('tractiune')}>
                <span style={styles.sectionTitle}>
                    <span style={styles.sectionIcon}><TbWheel size={18} color={primaryOrange} /></span>
                    Tractiune
                </span>
                <i className={`bi bi-chevron-${openSections.tractiune ? 'up' : 'down'}`} style={{color: '#aaa', fontSize: '0.8rem'}}></i>
            </div>
            {openSections.tractiune && (
                <div className="mt-3">
                    <div className="d-flex flex-wrap gap-2">
                        {uniqueDrivetrains.map(tractiune => (
                            <button
                                key={tractiune}
                                // MODIFICAT AICI: .includes()
                                style={filtre.tractiune.includes(tractiune) ? styles.tagBtnActive : styles.tagBtn}
                                onClick={() => toggleTag('tractiune', tractiune)}
                            >
                                {/* MODIFICAT AICI: .includes() */}
                                {filtre.tractiune.includes(tractiune) && <i className="bi bi-check-circle-fill me-1" style={{color: primaryOrange, fontSize: '0.65rem'}}></i>}
                                {tractiune}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="bg-light" style={{minHeight: '100vh'}}>
            {/* Hero Section — poza e self-hosted în public/ (hero.jpg + hero-mobile.jpg);
                înlocuiește ambele fișiere cu o poză reală din showroom când există. */}
            <div className="position-relative page-hero" style={{
                backgroundImage: `url("${isMobile ? '/hero-mobile.jpg' : '/hero.jpg'}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                paddingTop: '120px',
                paddingBottom: '3rem'
            }}>
                <div className="position-absolute top-0 start-0 w-100 h-100" style={{background: `linear-gradient(135deg, ${trustNavy}f0 0%, ${trustNavy}cc 100%)`}}></div>
                <div className="container position-relative text-center" style={{ paddingTop: isMobile ? '8px' : '3rem', paddingBottom: isMobile ? '24px' : '3rem' }}>
                    {isMobile && (
                        <img
                            src="/cheapcarslogo.png"
                            alt="SPCheapCars"
                            style={{
                                width: '180px', maxWidth: '64%', height: 'auto',
                                marginBottom: '16px',
                                filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.35))'
                            }}
                        />
                    )}
                    <h1 className="mb-3" style={{fontWeight: '800', letterSpacing: '3px', fontSize: 'clamp(2.2rem, 5vw, 3.5rem)', textTransform: 'uppercase', color: '#ffffff', lineHeight: '1.15'}}>Showroom Online</h1>
                    <div className="d-flex justify-content-center mb-4"><div style={{height: '4px', width: '80px', background: `linear-gradient(90deg, transparent 0%, ${primaryOrange} 50%, transparent 100%)`, borderRadius: '2px'}}></div></div>
                    <p style={{color: 'rgba(255,255,255,0.7)', fontSize: '1.125rem', fontWeight: '400'}}>Calitate certificată. Transparență totală.</p>

                    {/* Quick stats - Cele 3 patrate rotunjite */}
                    <div className="d-flex flex-wrap justify-content-center mt-4 mt-md-5" style={{gap: '12px'}}>
                        {[
                            { icon: 'bi-shield-check', value: '100%', label: 'Verificate' },
                            { icon: null, img: 'carverticallogo.png', value: 'CarVertical', label: 'Inclus' }
                        ].map((stat, idx) => (
                            <div
                                key={idx}
                                className="d-flex align-items-center gap-2 gap-md-3 px-3 px-md-4 py-2 py-md-3"
                                style={{
                                    background: 'rgba(255,255,255,0.1)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    flex: '0 1 auto',
                                    minWidth: isMobile ? 'calc(45% - 6px)' : 'auto',
                                    maxWidth: isMobile ? '200px' : '180px'
                                }}
                            >
                                {stat.img ? (
                                    <img src={stat.img} alt="" style={{width: '28px', height: '28px', objectFit: 'contain', filter: 'sepia(1) saturate(3) hue-rotate(350deg) brightness(1.1)'}} />
                                ) : (
                                    <i className={`bi ${stat.icon}`} style={{color: primaryOrange, fontSize: '1.3rem'}}></i>
                                )}
                                <div>
                                    <div className="text-white fw-bold" style={{fontSize: '1.125rem', lineHeight: 1}}>{stat.value}</div>
                                    <div style={{color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px'}}>{stat.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Scroll cue to car list (mobile only) — glass pill matching the quick stats */}
                    <div
                        className="mt-4 d-md-none"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '10px',
                            background: 'rgba(255,255,255,0.1)',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            borderRadius: '12px',
                            border: '1px solid rgba(255,255,255,0.1)',
                            padding: '12px 22px',
                            cursor: 'pointer'
                        }}
                        onClick={scrollToMasini}
                    >
                        <i className="bi bi-lightning-charge-fill" style={{ color: primaryOrange, fontSize: '1.1rem' }}></i>
                        <span className="text-white fw-bold" style={{ fontSize: '1rem' }}>Stoc Actualizat</span>
                        <i className="bi bi-chevron-down" style={{ color: primaryOrange, fontSize: '1rem' }}></i>
                    </div>
                </div>
            </div>

            <div className="container" style={{ paddingTop: '3rem', paddingBottom: isMobile ? '8px' : '3rem' }}>
                <div className="row g-4">

                    {/* ==================== DESKTOP FILTER PANEL ==================== */}
                    <div className="col-lg-3 d-none d-lg-block">
                        <div style={styles.filterPanel} className="hide-scrollbar">
                            <div style={styles.filterHeader}>
                                <div style={styles.filterHeaderPattern}></div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 style={styles.filterTitle}><div style={styles.filterIcon}><i className="bi bi-sliders" style={{color: primaryOrange}}></i></div>Filtrare</h6>
                                    <button style={styles.resetBtn} onClick={resetFilters}><i className="bi bi-arrow-counterclockwise me-1"></i>Reset</button>
                                </div>
                            </div>
                            {renderFilters()}
                        </div>
                    </div>

                    {/* ==================== VEHICLE GRID ==================== */}
                    <div className="col-lg-9">
                        <div style={styles.resultsHeader} id="masini">
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                <div style={{width: '40px', height: '40px', borderRadius: '10px', background: `linear-gradient(135deg, ${primaryOrange}15 0%, ${primaryOrange}05 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <i className="bi bi-grid-3x3-gap" style={{color: primaryOrange, fontSize: '1.1rem'}}></i>
                                </div>
                                <div>
                                    <h5 className="m-0 fw-bold" style={{color: trustNavy, fontSize: '1.125rem'}}>Rezultate</h5>
                                    <small style={{color: '#6b7280'}}><span style={{color: primaryOrange, fontWeight: '700'}}>{masiniFiltrate.length}</span> vehicule găsite</small>
                                </div>
                            </div>

                            <button
                                className="d-lg-none btn d-flex align-items-center gap-2 shadow-sm"
                                style={{
                                    backgroundColor: trustNavy,
                                    color: 'white',
                                    borderRadius: '10px',
                                    padding: '8px 16px',
                                    fontWeight: '600',
                                    border: 'none'
                                }}
                                onClick={() => setShowMobileFilters(true)}
                            >
                                <TbFilter size={18} />
                                Filtre
                            </button>

                            <div className="d-none d-md-block">
                                <select
                                    style={{
                                        border: '1px solid #e8e8e8',
                                        borderRadius: '10px',
                                        padding: '10px 16px',
                                        fontSize: '0.875rem',
                                        background: '#fafafa',
                                        cursor: 'pointer',
                                        color: '#555',
                                        fontWeight: '500'
                                    }}
                                    value={sortOrder}
                                    onChange={(e) => setSortOrder(e.target.value)}
                                >
                                    <option value="default">Sortare</option>
                                    <option value="pret_crescator">Preț Crescător</option>
                                    <option value="pret_descrescator">Preț Descrescător</option>
                                </select>
                            </div>
                        </div>

                        {fetchError && !loadingCars && allMasini.length === 0 ? (
                            <div className="text-center py-5" style={{background: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)'}}>
                                <div style={{width: '80px', height: '80px', borderRadius: '50%', background: `linear-gradient(135deg, #ef444415 0%, #ef444405 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'}}>
                                    <i className="bi bi-wifi-off" style={{fontSize: '2rem', color: '#ef4444'}}></i>
                                </div>
                                <h4 className="fw-bold mb-2" style={{color: trustNavy}}>Nu s-a putut conecta la server</h4>
                                <p className="text-muted mb-4" style={{maxWidth: '400px', margin: '0 auto 24px'}}>Verifică conexiunea la internet sau încearcă din nou.</p>
                                <button onClick={() => { setFetchError(false); setLoadingCars(true); apiGet('/parc-auto').then(d => { setAllMasini(d); setMasiniAfisat(d.filter(m => m.status !== STATUS.INACTIV)); }).catch(() => setFetchError(true)).finally(() => setLoadingCars(false)); }} style={{background: `linear-gradient(135deg, ${primaryOrange} 0%, #e67e00 100%)`, border: 'none', borderRadius: '10px', padding: '12px 28px', color: 'white', fontWeight: '600', cursor: 'pointer', boxShadow: `0 4px 15px ${primaryOrange}40`, transition: 'all 0.2s ease'}}>
                                    <i className="bi bi-arrow-clockwise me-2"></i>Încearcă din nou
                                </button>
                            </div>
                        ) : loadingCars ? (
                            <div className="row g-4">
                                {[1,2,3,4,5,6].map(i => (
                                    <div key={i} className="col-md-6 col-xl-4">
                                        <div style={{ ...styles.vehicleCard, overflow: 'hidden' }}>
                                            <div style={{ aspectRatio: '4 / 3', background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
                                            <div style={{ padding: '20px' }}>
                                                <div style={{ height: '14px', background: '#f0f0f0', borderRadius: '4px', width: '60%', marginBottom: '8px' }} />
                                                <div style={{ height: '12px', background: '#f0f0f0', borderRadius: '4px', width: '40%', marginBottom: '16px' }} />
                                                <div style={{ height: '50px', background: '#f0f0f0', borderRadius: '12px' }} />
                                            </div>
                                            <div style={{ padding: '12px 20px', background: '#f8f9fa', display: 'flex', justifyContent: 'space-between' }}>
                                                <div style={{ height: '12px', background: '#f0f0f0', borderRadius: '4px', width: '25%' }} />
                                                <div style={{ height: '12px', background: '#f0f0f0', borderRadius: '4px', width: '25%' }} />
                                                <div style={{ height: '12px', background: '#f0f0f0', borderRadius: '4px', width: '25%' }} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (<>
                        <div className="row g-4">
                            {masiniDisplay.map((masina) => (
                                <div key={masina.id} className="col-md-6 col-xl-4">
                                    <div style={styles.vehicleCard} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.12)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.06)'; }} onTouchStart={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.12)'; }} onTouchEnd={(e) => { const el = e.currentTarget; setTimeout(() => { el.style.transform = 'translateY(0)'; el.style.boxShadow = '0 4px 20px rgba(0,0,0,0.06)'; }, 200); }}>
                                        <Link to={`/masina/${generateSlug(masina)}`} state={{ carId: masina.id }} className="d-block" style={styles.imageWrapper}>
                                            <img src={getImaginePrincipala(masina)} alt={`${masina.marca} ${masina.model} ${masina.an}`} className="w-100 h-100" loading="lazy" style={{objectFit: 'cover', transition: 'transform 0.5s ease'}} />
                                            <div style={styles.yearBadge}>{masina.an}</div>
                                            {masina.tvaReductibil && <div className="tva-ribbon">TVA Deductibil</div>}
                                        </Link>
                                        <div style={styles.cardBody}>
                                            <Link to={`/masina/${generateSlug(masina)}`} state={{ carId: masina.id }} className="text-decoration-none">
                                                <h5 className="fw-bold m-0" style={{color: trustNavy, fontSize: '1rem', lineHeight: '1.3'}}>
                                                    <span className="d-block">{masina.marca}</span>
                                                    <span className="d-block" style={{color: primaryOrange}}>{masina.model}</span>
                                                </h5>
                                            </Link>
                                            <p className="m-0 mt-1" style={{color: '#6b7280', fontSize: '0.75rem'}}>{masina.motor ? `${masina.motor} cm3` : ''}{masina.putere ? ` - ${masina.putere} CP` : ''}</p>
                                            <div style={styles.priceBox}>
                                                <div style={styles.priceBoxAccent}></div>
                                                <div className="d-flex align-items-baseline w-100">
                                                    <span style={styles.price}>{masina.pret ? formatPret(masina.pret) : '0'}</span><span style={styles.priceCurrency}>EUR</span>
                                                </div>
                                            </div>
                                            <div className="mt-3 text-center py-2 px-3" style={{background: `linear-gradient(135deg, ${primaryOrange}08 0%, ${primaryOrange}03 100%)`, borderRadius: '8px', border: `1px solid ${primaryOrange}15`}}>
                                                <span style={{color: trustNavy, fontSize: '0.75rem', fontWeight: '600'}}><img src="carverticallogo.png" alt="CarVertical" style={{width: '14px', height: '14px', objectFit: 'contain', filter: 'sepia(1) saturate(3) hue-rotate(350deg) brightness(1.1)', marginRight: '4px'}} />CarVertical Inclus - Garantie</span>
                                            </div>
                                        </div>
                                        <div style={styles.cardFooter}>
                                            <div style={styles.footerStat}><TbRoad size={14} color={primaryOrange} />{formatNumar(masina.kilometraj)} km</div>
                                            <div style={{width: '1px', height: '16px', background: '#e0e0e0'}}></div>
                                            <div style={styles.footerStat}><TbGasStation size={15} color={primaryOrange} />{combustibilScurt(masina.combustibil)}</div>
                                            <div style={{width: '1px', height: '16px', background: '#e0e0e0'}}></div>
                                            <div style={styles.footerStat}><TbManualGearbox size={15} color={primaryOrange} />{masina.cutieViteze}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {masiniFiltrate.length === 0 && (
                            <div className="text-center py-5" style={{background: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '2px dashed #e0e0e0'}}>
                                <div style={{width: '80px', height: '80px', borderRadius: '50%', background: `linear-gradient(135deg, ${primaryOrange}15 0%, ${primaryOrange}05 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'}}>
                                    <i className="bi bi-search" style={{fontSize: '2rem', color: primaryOrange}}></i>
                                </div>
                                <h4 className="fw-bold mb-2" style={{color: trustNavy}}>Niciun rezultat găsit</h4>
                                <p className="text-muted mb-4">Încearcă să modifici criteriile de căutare</p>
                                <button onClick={resetFilters} style={{background: `linear-gradient(135deg, ${primaryOrange} 0%, #e67e00 100%)`, border: 'none', borderRadius: '10px', padding: '12px 28px', color: 'white', fontWeight: '600', cursor: 'pointer', boxShadow: `0 4px 15px ${primaryOrange}40`, transition: 'all 0.2s ease'}}>
                                    <i className="bi bi-arrow-counterclockwise me-2"></i>Resetează filtrele
                                </button>
                            </div>
                        )}

                        {isMobile && totalPages > 1 && (
                            <div className="d-flex align-items-center justify-content-center gap-3 mt-4">
                                <button
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    style={{
                                        width: '44px', height: '44px', borderRadius: '50%',
                                        background: '#fff', border: 'none',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        opacity: currentPage === 1 ? 0.4 : 1,
                                        pointerEvents: currentPage === 1 ? 'none' : 'auto',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <i className="bi bi-chevron-left" style={{color: trustNavy, fontSize: '1.1rem'}}></i>
                                </button>
                                <span style={{color: trustNavy, fontWeight: '600', fontSize: '0.875rem'}}>
                                    Pagina {currentPage} din {totalPages}
                                </span>
                                <button
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    style={{
                                        width: '44px', height: '44px', borderRadius: '50%',
                                        background: '#fff', border: 'none',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        opacity: currentPage === totalPages ? 0.4 : 1,
                                        pointerEvents: currentPage === totalPages ? 'none' : 'auto',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <i className="bi bi-chevron-right" style={{color: trustNavy, fontSize: '1.1rem'}}></i>
                                </button>
                            </div>
                        )}
                    </>)}
                    </div>
                </div>
            </div>

            {showMobileFilters && (
                <>
                    {/* Backdrop */}
                    <div
                        onClick={() => setShowMobileFilters(false)}
                        style={{
                            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                            background: 'rgba(0,0,0,0.55)', zIndex: 9998,
                            backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)'
                        }}
                    />

                    {/* Bottom sheet */}
                    <div
                        style={{
                            position: 'fixed', bottom: 0, left: 0, right: 0,
                            height: '90vh',
                            background: '#fff',
                            borderRadius: '20px 20px 0 0',
                            zIndex: 9999,
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 -8px 40px rgba(0,0,0,0.2)',
                            transform: `translateY(${dragY}px)`,
                            transition: isDragging.current ? 'none' : 'transform 0.3s ease'
                        }}
                    >
                        {/* Handle — drag zone */}
                        <div
                            className="d-flex justify-content-center pt-3 pb-1"
                            style={{ touchAction: 'none' }}
                            onTouchStart={onSheetTouchStart}
                            onTouchMove={onSheetTouchMove}
                            onTouchEnd={onSheetTouchEnd}
                        >
                            <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#e0e0e0' }} />
                        </div>

                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{borderBottom: '1px solid #f0f0f0'}}>
                            <div>
                                <h5 className="m-0 fw-bold" style={{color: trustNavy}}>Filtre</h5>
                                {(() => {
                                    const activeCount = Object.values(filtre).filter(v => Array.isArray(v) ? v.length > 0 : v !== "").length;
                                    return activeCount > 0 ? (
                                        <span style={{fontSize: '0.75rem', color: primaryOrange, fontWeight: '600'}}>{activeCount} active</span>
                                    ) : (
                                        <span style={{fontSize: '0.75rem', color: '#6b7280'}}>Niciun filtru activ</span>
                                    );
                                })()}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <button onClick={resetFilters} style={{
                                    background: 'none', border: `1px solid #e0e0e0`,
                                    borderRadius: '20px', padding: '6px 14px',
                                    fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', cursor: 'pointer'
                                }}>Resetează</button>
                                <button onClick={() => setShowMobileFilters(false)} style={{
                                    width: '36px', height: '36px', borderRadius: '50%',
                                    background: '#f5f5f5', border: 'none',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                                }}>
                                    <TbX size={20} color="#555" />
                                </button>
                            </div>
                        </div>

                        {/* Scrollable filter content */}
                        <div style={{flex: 1, overflowY: 'auto', padding: '8px 0 16px'}}>
                            {/* Sortare — doar pe mobil; pe desktop există dropdown-ul din header */}
                            <div style={{ padding: '16px 20px 0' }}>
                                <span style={styles.sectionTitle}>
                                    <span style={styles.sectionIcon}><TbArrowsSort size={18} color={primaryOrange} /></span>
                                    Sortare
                                </span>
                                <div className="d-flex flex-wrap gap-2 mt-3">
                                    {[
                                        { value: 'default', label: 'Recomandate' },
                                        { value: 'pret_crescator', label: 'Preț crescător' },
                                        { value: 'pret_descrescator', label: 'Preț descrescător' }
                                    ].map(opt => (
                                        <button
                                            key={opt.value}
                                            style={sortOrder === opt.value ? styles.tagBtnActive : styles.tagBtn}
                                            onClick={() => setSortOrder(opt.value)}
                                        >
                                            {sortOrder === opt.value && <i className="bi bi-check-circle-fill me-1" style={{color: primaryOrange, fontSize: '0.65rem'}}></i>}
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                                <div style={{ height: '1px', background: '#f0f0f0', marginTop: '16px' }} />
                            </div>
                            {renderFilters()}
                        </div>

                        {/* Sticky CTA */}
                        <div style={{
                            padding: '12px 16px',
                            paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
                            borderTop: '1px solid #f0f0f0',
                            background: '#fff'
                        }}>
                            <button
                                onClick={() => setShowMobileFilters(false)}
                                style={{
                                    width: '100%', padding: '16px',
                                    background: `linear-gradient(135deg, ${trustNavy} 0%, #2a4266 100%)`,
                                    border: 'none', borderRadius: '14px',
                                    color: '#fff', fontSize: '1rem', fontWeight: '700',
                                    cursor: 'pointer', letterSpacing: '0.3px'
                                }}
                            >
                                Vezi {masiniFiltrate.length} {masiniFiltrate.length === 1 ? 'Rezultat' : 'Rezultate'}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default Home;
